import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  Select,
  Textarea,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {api} from 'api/setup';
import {IModalProps, Modal} from 'components/shared';
import React, {FC, useState} from 'react';
import {modalMetroCloseButtonStyles, pinkScrollBarStyles} from 'theme';
import {IProduct} from 'types';
import ImageUI from 'ui/ImageUI';

import {useTranslation} from '../../../../hooks';

interface IExtendedProduct extends IProduct {
  name_ru?: string;
  name_ro?: string;
  name_en?: string;
  details_ru?: string;
  details_ro?: string;
  details_en?: string;
  storage_instructions_ru?: string;
  storage_instructions_ro?: string;
  storage_instructions_en?: string;
  ingredients_csv_ru?: string;
  ingredients_csv_ro?: string;
  ingredients_csv_en?: string;
}

interface IEditFormData {
  name_ru: string;
  name_ro: string;
  name_en: string;
  details_ru: string;
  details_ro: string;
  details_en: string;
  storage_instructions_ru: string;
  storage_instructions_ro: string;
  storage_instructions_en: string;
  ingredients_csv_ru: string;
  ingredients_csv_ro: string;
  ingredients_csv_en: string;
  quantity_value: number | null;
  mass_measurement: string;
}

interface Props extends IModalProps {
  product: IExtendedProduct;
  onSuccess: () => void;
  onCloseParent: () => void;
}

export const MetroProductEditModal: FC<Props> = ({isOpen, onClose, product, onSuccess, onCloseParent}) => {
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  // Mass measurement options
  const MASS_MEASUREMENT_OPTIONS = [
    {value: '0', label: 'gr'},
    {value: '1', label: 'ml'},
    {value: '2', label: 'l'},
    {value: '3', label: 'kg'},
    {value: '4', label: 'items'},
  ];

  const [formData, setFormData] = useState<IEditFormData>({
    name_ru: product.name_ru || '',
    name_ro: product.name_ro || '',
    name_en: product.name_en || '',
    details_ru: product.details_ru || '',
    details_ro: product.details_ro || '',
    details_en: product.details_en || '',
    storage_instructions_ru: product.storage_instructions_ru || '',
    storage_instructions_ro: product.storage_instructions_ro || '',
    storage_instructions_en: product.storage_instructions_en || '',
    ingredients_csv_ru: product.ingredients_csv_ru || '',
    ingredients_csv_ro: product.ingredients_csv_ro || '',
    ingredients_csv_en: product.ingredients_csv_en || '',
    quantity_value: product.quantity_value || null,
    mass_measurement: product.mass_measurement2 != undefined ? product.mass_measurement2 : '',
  });

  const handleInputChange = (field: keyof IEditFormData, value: string | number | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const product_data = {
        id: product.id,
        name_ru: formData.name_ru,
        name_ro: formData.name_ro,
        name_en: formData.name_en,
        details_en: formData.details_en,
        details_ro: formData.details_ro,
        details_ru: formData.details_ru,
        storage_instructions_en: formData.storage_instructions_en,
        storage_instructions_ro: formData.storage_instructions_ro,
        storage_instructions_ru: formData.storage_instructions_ru,
        ingredients_csv_ru: formData.ingredients_csv_ru,
        ingredients_csv_ro: formData.ingredients_csv_ro,
        ingredients_csv_en: formData.ingredients_csv_en,
        quantity_value: formData.quantity_value,
        mass_measurement: formData.mass_measurement,
      };

      await api.post('/market-product/edit-product', product_data);
      onSuccess();
      onClose();
      onCloseParent(); // Close the parent modal as well
    } catch (error) {
      console.error('Error updating product:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const languages = [
    {code: 'ru', label: 'Русский'},
    {code: 'ro', label: 'Română'},
    {code: 'en', label: 'English'},
  ];

  const fields = [
    {key: 'name', label: 'Name', type: 'input'},
    {key: 'details', label: 'Details', type: 'textarea'},
    {key: 'storage_instructions', label: 'Storage Instructions', type: 'textarea'},
    {key: 'ingredients_csv', label: 'Ingredients', type: 'textarea'},
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior>
      <ModalContent
        h={['100vh', '90vh']}
        overflow="hidden"
        borderRadius="3xl"
        maxW={['100%', '1200px']}
        display="flex"
        flexDirection="column"
      >
        <ModalCloseButton sx={modalMetroCloseButtonStyles} zIndex={10} />
        <ModalBody p={6} flex="1" overflowY="auto" sx={pinkScrollBarStyles}>
          <VStack align="stretch" spacing={6}>
            <Heading fontSize="2xl" mb={4}>
              Edit Product: {product.name} ({product.article_number})
            </Heading>

            {/* Language Fields */}
            {fields.map((field) => (
              <Box key={field.key} border="1px solid" borderColor="gray.200" borderRadius="lg" p={4}>
                <Heading fontSize="lg" mb={4}>
                  {field.label}
                </Heading>
                <VStack spacing={4}>
                  {languages.map((lang) => {
                    const fieldKey = `${field.key}_${lang.code}` as keyof IEditFormData;
                    return (
                      <FormControl key={lang.code}>
                        <FormLabel>
                          {field.label} ({lang.label})
                        </FormLabel>
                        {field.type === 'input' ? (
                          <Input
                            value={formData[fieldKey] as string}
                            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          />
                        ) : (
                          <Textarea
                            value={formData[fieldKey] as string}
                            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                            rows={3}
                          />
                        )}
                      </FormControl>
                    );
                  })}
                </VStack>
              </Box>
            ))}
            {/* General Fields */}
            <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4}>
              <Heading fontSize="lg" mb={4}>
                General Information
              </Heading>
              <HStack spacing={4} mb={4}>
                <FormControl>
                  <FormLabel>Quantity Value</FormLabel>
                  <Input
                    type="number"
                    value={formData.quantity_value || ''}
                    onChange={(e) =>
                      handleInputChange('quantity_value', e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Mass Measurement</FormLabel>
                  <Select
                    value={formData.mass_measurement}
                    onChange={(e) => handleInputChange('mass_measurement', e.target.value)}
                    placeholder="Select measurement type"
                  >
                    {MASS_MEASUREMENT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
            </Box>

            {/* Action Buttons */}
            <HStack spacing={4} justifyContent="flex-end" pt={4}>
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                variant="primary"
                colorScheme="pink"
                onClick={handleSubmit}
                isLoading={isLoading}
                loadingText="Saving..."
              >
                {t('SAVE')}
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// Component for the edit button with integrated modal
interface EditButtonProps {
  product: IExtendedProduct;
  onSuccess: () => void;
  onCloseParent: () => void;
}

export const ProductEditButton: FC<EditButtonProps> = ({product, onSuccess, onCloseParent}) => {
  const {isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal} = useDisclosure();

  return (
    <>
      <IconButton
        aria-label="Edit product"
        icon={<ImageUI src="/edit-icon.svg" alt="Edit" />}
        size="sm"
        variant="solid"
        onClick={onOpenEditModal}
        borderRadius="full"
      />
      {isOpenEditModal && (
        <MetroProductEditModal
          isOpen={isOpenEditModal}
          onClose={onCloseEditModal}
          product={product}
          onSuccess={onSuccess}
          onCloseParent={onCloseParent}
        />
      )}
    </>
  );
};
